"use client";

import Script from "next/script";
import { useEffect, useRef, useState } from "react";
import { COOKIE_CONSENT_EVENT, readCookieConsent, type CookieConsent } from "@/lib/cookie-consent";
import { ensureGtagWithDeniedDefaults, GOOGLE_ADS_ID, isGoogleAdsEnabled } from "@/lib/google-ads";

// Google Ads tag under Consent Mode v2. gtag.js loads only after marketing consent.
export function GoogleAdsTag() {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const configured = useRef(false);
  const enabled = isGoogleAdsEnabled();

  useEffect(() => {
    if (!enabled) return;
    ensureGtagWithDeniedDefaults();
    setConsent(readCookieConsent());
    const onChange = (event: Event) => setConsent((event as CustomEvent<CookieConsent>).detail);
    window.addEventListener(COOKIE_CONSENT_EVENT, onChange as EventListener);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, onChange as EventListener);
  }, [enabled]);

  const marketing = Boolean(consent?.marketing);

  useEffect(() => {
    if (!consent || typeof window.gtag !== "function") return;
    const ads = consent.marketing ? "granted" : "denied";
    window.gtag("consent", "update", {
      analytics_storage: consent.analytics ? "granted" : "denied",
      ad_storage: ads,
      ad_user_data: ads,
      ad_personalization: ads,
    });
    if (consent.marketing && !configured.current) {
      configured.current = true;
      // Pass an explicit params object. Firebase Analytics wraps window.gtag and forwards
      // a missing third argument as `undefined`, which crashes gtag.js ("Cannot convert
      // undefined or null to object") so the Google Ads tag never configured on CLabs.
      window.gtag("config", GOOGLE_ADS_ID, {});
    }
  }, [consent]);

  if (!enabled || !marketing) return null;
  return <Script src={`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`} strategy="afterInteractive" />;
}
